import { projectApi } from './projectApi'
import { dashboardApi } from './dashboardApi'
import { checklistApi } from './checklistApi'
import { issueApi } from './issueApi'
import { systemApi } from './systemApi'
import type { Project, DashboardStats, Checklist, Issue, System } from '../types'

function escapeHtml(str: string | null | undefined): string {
  if (!str) return ''
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}

function renderSystemsTree(systems: System[], depth = 0): string {
  let html = ''
  for (const sys of systems) {
    const indent = depth * 24
    html += `<tr>
      <td style="padding-left: ${indent + 8}px;">${escapeHtml(sys.code)}</td>
      <td>${escapeHtml(sys.name)}</td>
      <td>${escapeHtml(sys.description)}</td>
      <td style="text-align:center;">${sys.equipmentCount ?? 0}</td>
      <td>${escapeHtml(sys.status)}</td>
    </tr>`
    if (sys.children && sys.children.length > 0) {
      html += renderSystemsTree(sys.children, depth + 1)
    }
  }
  return html
}

function buildReportHtml(
  project: Project,
  stats: DashboardStats,
  checklists: Checklist[],
  issues: Issue[],
  systems: System[],
): string {
  const now = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })

  const openIssues = issues.filter(i => i.status === 'Open' || i.status === 'InProgress' || i.status === 'PendingResponse')

  const priorityOrder: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 }
  const sortByPriority = <T extends { priority: string }>(a: T, b: T) =>
    (priorityOrder[a.priority] ?? 99) - (priorityOrder[b.priority] ?? 99)

  openIssues.sort(sortByPriority)

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Commissioning Report — ${escapeHtml(project.name)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    color: #1a1a1a;
    background: #fff;
    font-size: 11pt;
    line-height: 1.5;
    padding: 0;
  }
  .page { padding: 40px 48px; }
  .no-print { }

  /* Header */
  .report-header {
    border-bottom: 3px solid #1e3a5f;
    padding-bottom: 16px;
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .report-header h1 {
    font-size: 22pt;
    color: #1e3a5f;
    margin-bottom: 4px;
  }
  .report-header .subtitle {
    font-size: 10pt;
    color: #555;
  }
  .logo-placeholder {
    width: 120px;
    height: 48px;
    background: #1e3a5f;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 16pt;
    letter-spacing: 2px;
    border-radius: 4px;
  }
  .meta-table {
    width: 100%;
    margin-bottom: 24px;
    border-collapse: collapse;
  }
  .meta-table td {
    padding: 4px 12px 4px 0;
    font-size: 10pt;
  }
  .meta-table .label {
    font-weight: 600;
    color: #555;
    width: 140px;
  }

  /* Section headers */
  .section-title {
    font-size: 14pt;
    color: #1e3a5f;
    border-bottom: 2px solid #d0d7de;
    padding-bottom: 6px;
    margin: 32px 0 16px 0;
  }
  .section-break {
    page-break-before: always;
  }

  /* Summary cards */
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
    margin-bottom: 24px;
  }
  .summary-card {
    border: 1px solid #d0d7de;
    border-radius: 6px;
    padding: 14px 16px;
    text-align: center;
  }
  .summary-card .value {
    font-size: 20pt;
    font-weight: 700;
    color: #1e3a5f;
  }
  .summary-card .label {
    font-size: 9pt;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* Tables */
  table.data-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 24px;
    font-size: 9.5pt;
  }
  table.data-table th {
    background: #1e3a5f;
    color: #fff;
    font-weight: 600;
    text-align: left;
    padding: 8px 10px;
    font-size: 9pt;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  table.data-table td {
    padding: 7px 10px;
    border-bottom: 1px solid #e1e4e8;
    vertical-align: top;
  }
  table.data-table tr:nth-child(even) td {
    background: #f6f8fa;
  }
  table.data-table tr:hover td {
    background: #eef2f7;
  }

  /* Priority badges */
  .badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 8.5pt;
    font-weight: 600;
    text-transform: uppercase;
  }
  .badge-critical { background: #fdd; color: #a00; }
  .badge-high { background: #ffe0cc; color: #b34700; }
  .badge-medium { background: #fff3cd; color: #856404; }
  .badge-low { background: #d4edda; color: #155724; }
  .badge-open { background: #cce5ff; color: #004085; }
  .badge-inprogress { background: #fff3cd; color: #856404; }
  .badge-resolved { background: #d4edda; color: #155724; }
  .badge-closed { background: #e2e3e5; color: #383d41; }
  .badge-deferred { background: #e2e3e5; color: #6c757d; }
  .badge-complete { background: #d4edda; color: #155724; }
  .badge-pendingresponse { background: #f8d7da; color: #721c24; }
  .badge-notstarted { background: #e2e3e5; color: #383d41; }

  /* Print button */
  .print-bar {
    background: #f0f2f5;
    padding: 12px 48px;
    text-align: right;
    border-bottom: 1px solid #d0d7de;
  }
  .print-bar button {
    background: #1e3a5f;
    color: #fff;
    border: none;
    padding: 8px 24px;
    font-size: 10pt;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
  }
  .print-bar button:hover { background: #15304d; }

  /* Footer */
  .report-footer {
    margin-top: 40px;
    padding-top: 12px;
    border-top: 1px solid #d0d7de;
    font-size: 8.5pt;
    color: #888;
    text-align: center;
  }

  /* Empty state */
  .empty-msg {
    padding: 16px;
    color: #888;
    font-style: italic;
  }

  /* Print styles */
  @media print {
    .no-print { display: none !important; }
    .print-bar { display: none !important; }
    body { font-size: 10pt; }
    .page { padding: 24px 32px; }
    table.data-table tr:hover td { background: inherit; }
    table.data-table tr:nth-child(even) td { background: #f6f8fa; }
    .summary-card .value { color: #1e3a5f; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table.data-table th { background: #1e3a5f !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>

<div class="print-bar no-print">
  <button onclick="window.print()">Print Report</button>
</div>

<div class="page">

  <!-- Header -->
  <div class="report-header">
    <div>
      <h1>Commissioning Report</h1>
      <div class="subtitle">${escapeHtml(project.name)}</div>
    </div>
    <div class="logo-placeholder">ISE</div>
  </div>

  <table class="meta-table">
    <tr><td class="label">Project Number</td><td>${escapeHtml(project.projectNumber)}</td><td class="label">Client</td><td>${escapeHtml(project.client)}</td></tr>
    <tr><td class="label">Location</td><td>${escapeHtml(project.location)}</td><td class="label">Status</td><td>${escapeHtml(project.status)}</td></tr>
    <tr><td class="label">Start Date</td><td>${formatDate(project.startDate)}</td><td class="label">End Date</td><td>${formatDate(project.endDate)}</td></tr>
    <tr><td class="label">Report Generated</td><td colspan="3">${escapeHtml(now)}</td></tr>
  </table>

  <!-- Executive Summary -->
  <h2 class="section-title">Executive Summary</h2>
  <div class="summary-grid">
    <div class="summary-card"><div class="value">${stats.totalSystems}</div><div class="label">Systems</div></div>
    <div class="summary-card"><div class="value">${stats.totalEquipment}</div><div class="label">Equipment</div></div>
    <div class="summary-card"><div class="value">${stats.totalChecklists}</div><div class="label">Checklists</div></div>
    <div class="summary-card"><div class="value">${Math.round(stats.checklistCompletion)}%</div><div class="label">Checklist Completion</div></div>
    <div class="summary-card"><div class="value">${stats.openIssues}</div><div class="label">Open Issues</div></div>
  </div>

  <!-- Checklist Status -->
  <h2 class="section-title section-break">Checklist Status</h2>
  ${checklists.length > 0 ? `
  <table class="data-table">
    <thead>
      <tr><th>Name</th><th>Template</th><th>Status</th><th>Completion</th><th>Assigned To</th></tr>
    </thead>
    <tbody>
      ${checklists.map(cl => `<tr>
        <td>${escapeHtml(cl.name)}</td>
        <td>${escapeHtml(cl.templateName)}</td>
        <td><span class="badge badge-${cl.status.toLowerCase().replace(/\s+/g, '')}">${escapeHtml(cl.status)}</span></td>
        <td>${cl.completedItems}/${cl.totalItems} (${Math.round(cl.completionPercentage)}%)</td>
        <td>${escapeHtml(cl.assignedTo)}</td>
      </tr>`).join('')}
    </tbody>
  </table>` : '<p class="empty-msg">No checklists found for this project.</p>'}

  <!-- Issues Summary -->
  <h2 class="section-title section-break">Issues Summary</h2>
  ${openIssues.length > 0 ? `
  <table class="data-table">
    <thead>
      <tr><th>Number</th><th>Title</th><th>Type</th><th>Category</th><th>Priority</th><th>Status</th></tr>
    </thead>
    <tbody>
      ${openIssues.map(i => `<tr>
        <td>${escapeHtml(i.issueNumber)}</td>
        <td>${escapeHtml(i.title)}</td>
        <td>${escapeHtml(i.type)}</td>
        <td>${escapeHtml(i.category)}</td>
        <td><span class="badge badge-${i.priority.toLowerCase()}">${escapeHtml(i.priority)}</span></td>
        <td><span class="badge badge-${i.status.toLowerCase().replace(/\s+/g, '')}">${escapeHtml(i.status)}</span></td>
      </tr>`).join('')}
    </tbody>
  </table>` : '<p class="empty-msg">No open issues.</p>'}

  <!-- Systems Overview -->
  <h2 class="section-title section-break">Systems Overview</h2>
  ${systems.length > 0 ? `
  <table class="data-table">
    <thead>
      <tr><th>Code</th><th>Name</th><th>Description</th><th>Equipment</th><th>Status</th></tr>
    </thead>
    <tbody>
      ${renderSystemsTree(systems)}
    </tbody>
  </table>` : '<p class="empty-msg">No systems found for this project.</p>'}

  <div class="report-footer">
    InSynergy Engineering &mdash; Commissioning Report &mdash; Generated ${escapeHtml(now)}
  </div>

</div>

</body>
</html>`
}

export async function generateProjectReport(projectId: string): Promise<void> {
  const [project, stats, checklists, issues, systems] = await Promise.all([
    projectApi.getById(projectId),
    dashboardApi.getProjectSpecificStats(projectId),
    checklistApi.getByProject(projectId),
    issueApi.getByProject(projectId),
    systemApi.getTree(projectId),
  ])

  const html = buildReportHtml(project, stats, checklists, issues, systems)

  const reportWindow = window.open('', '_blank')
  if (reportWindow) {
    reportWindow.document.write(html)
    reportWindow.document.close()
  }
}
